// AI Costs Panel - Shows AI/LLM cost tracking data
//
// Combines BOTH cost pipelines (non-overlapping, so summing is exact):
// Agent Console + Co-Pilots (agent_console_message.cost_usd — the larger line)
// and AI/API background services (api_usage_logs: OpenAI, Document AI, …).
// The combined headline + 30-day trend come from /admin/costs/overview; the
// per-period breakdowns from /admin/costs/agent-console and /admin/costs/summary.

import { useEffect, useState, useCallback } from 'react';
import { costTrackingService } from '../../services/costTracking';
import type {
  AgentConsoleCostSummary,
  CostOverview,
  CostSummary,
  CostToday,
} from '../../services/costTracking';
import { formatMoney } from '../../utils/formatting';
import styles from './StatisticsTab.module.css';

interface CostsPanelProps {
  dateFrom: Date;
  dateTo: Date;
}

export default function CostsPanel({ dateFrom, dateTo }: CostsPanelProps) {
  const [today, setToday] = useState<CostToday | null>(null);
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [overview, setOverview] = useState<CostOverview | null>(null);
  const [agentConsole, setAgentConsole] = useState<AgentConsoleCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        costTrackingService.getToday(),
        costTrackingService.getSummary(dateFrom, dateTo),
        costTrackingService.getOverview(true),
        costTrackingService.getAgentConsole(dateFrom, dateTo),
      ]);

      if (results[0].status === 'fulfilled') setToday(results[0].value);
      if (results[1].status === 'fulfilled') setSummary(results[1].value);
      if (results[2].status === 'fulfilled') setOverview(results[2].value);
      if (results[3].status === 'fulfilled') setAgentConsole(results[3].value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        setError('Failed to load cost data');
      } else if (failures.length > 0) {
        setError(`Some cost data could not be loaded (${failures.length} of ${results.length} failed)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading cost data...</div>;

  const spikeMultiplier = today ? parseFloat(today.spike_multiplier) : 0;
  const isSpike = spikeMultiplier > 2;

  // Combined today headline; falls back to the AI/API-only figure when the
  // overview call failed so the card never silently shows $0.00.
  const todayWindow = overview?.windows?.today;
  const todayCombined = todayWindow?.combined_usd ?? today?.today_total_usd;

  // Period total = Agent Console + AI/API for the selected date range. When a
  // pipeline's call failed, show what loaded and say which line is missing —
  // never present a partial sum as the total.
  const acPeriod = agentConsole ? parseFloat(agentConsole.total_usd) || 0 : null;
  const apiPeriod = summary ? parseFloat(summary.totals.cost_usd) || 0 : null;
  const periodTotal = (acPeriod ?? 0) + (apiPeriod ?? 0);
  const periodScope =
    acPeriod !== null && apiPeriod !== null
      ? `Agent Console ${formatMoney(acPeriod)} · AI/API ${formatMoney(apiPeriod)}`
      : acPeriod === null
        ? 'AI/API services only — Agent Console spend unavailable'
        : 'Agent Console only — AI/API spend unavailable';

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {/* Today's KPIs */}
      {(today || overview) && (
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Today&apos;s Spend</span>
            <span className={styles.kpiValue}>{formatMoney(todayCombined)}</span>
            <span className={styles.kpiChange}>
              {todayWindow
                ? `Agent Console ${formatMoney(todayWindow.agent_console_usd)} · AI/API ${formatMoney(todayWindow.api_usd)}`
                : 'AI/API services only — Agent Console spend unavailable'}
            </span>
          </div>
          {today && (
            <>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Current Hour</span>
                <span className={styles.kpiValue}>{formatMoney(today.current_hour_usd)}</span>
                <span className={styles.kpiChange}>AI/API services pipeline</span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Avg Hourly (7d)</span>
                <span className={styles.kpiValue}>{formatMoney(today.avg_hourly_7d_usd)}</span>
                <span className={styles.kpiChange}>AI/API services pipeline</span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Spike Ratio</span>
                <span className={styles.kpiValue} style={isSpike ? { color: '#dc2626' } : undefined}>
                  {spikeMultiplier.toFixed(1)}x
                </span>
                {isSpike && (
                  <span className={`${styles.kpiChange} ${styles.kpiChangeNegative}`}>
                    Above normal hourly rate
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Period Summary */}
      {(summary || agentConsole) && (
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Period Total</span>
            <span className={styles.kpiValue}>{formatMoney(periodTotal)}</span>
            <span className={styles.kpiChange}>{periodScope}</span>
          </div>
          {agentConsole && (
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Agent Console Turns</span>
              <span className={styles.kpiValue}>{agentConsole.turn_count.toLocaleString()}</span>
            </div>
          )}
          {summary && (
            <>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>API Calls</span>
                <span className={styles.kpiValue}>{summary.totals.call_count.toLocaleString()}</span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Total Tokens</span>
                <span className={styles.kpiValue}>{summary.totals.total_tokens.toLocaleString()}</span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Pages Processed</span>
                <span className={styles.kpiValue}>{summary.totals.total_pages.toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Agent Console Breakdown */}
      {agentConsole && agentConsole.by_door.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Agent Console by Door</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Door</th>
                <th className={styles.numericCell}>Cost</th>
                <th className={styles.numericCell}>Turns</th>
              </tr>
            </thead>
            <tbody>
              {agentConsole.by_door.map((door) => (
                <tr key={door.door_id}>
                  <td>{door.label}</td>
                  <td className={styles.numericCell}>{formatMoney(door.cost_usd)}</td>
                  <td className={styles.numericCell}>{door.turn_count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Provider Breakdown (AI/API pipeline) */}
      {summary && Object.keys(summary.by_provider).length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>AI/API Cost by Provider</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Provider</th>
                <th className={styles.numericCell}>Cost</th>
                <th className={styles.numericCell}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.by_provider)
                .sort(([, a], [, b]) => parseFloat(b.cost_usd) - parseFloat(a.cost_usd))
                .map(([provider, data]) => (
                  <tr key={provider}>
                    <td>{provider}</td>
                    <td className={styles.numericCell}>{formatMoney(data.cost_usd)}</td>
                    <td className={styles.numericCell}>{data.count.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Operation Breakdown (AI/API pipeline) */}
      {summary && Object.keys(summary.by_operation).length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>AI/API Cost by Operation</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Operation</th>
                <th className={styles.numericCell}>Cost</th>
                <th className={styles.numericCell}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.by_operation)
                .sort(([, a], [, b]) => parseFloat(b.cost_usd) - parseFloat(a.cost_usd))
                .map(([operation, data]) => (
                  <tr key={operation}>
                    <td>{operation}</td>
                    <td className={styles.numericCell}>{formatMoney(data.cost_usd)}</td>
                    <td className={styles.numericCell}>{data.count.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Daily Trend — combined, both pipelines */}
      {overview?.daily && overview.daily.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Daily Spend (Last 30 Days, both pipelines)</div>
          <DailyCostChart data={overview.daily} />
        </div>
      )}
    </>
  );
}

function DailyCostChart({ data }: { data: NonNullable<CostOverview['daily']> }) {
  const maxCost = Math.max(...data.map((d) => parseFloat(d.combined_usd) || 0), 0.01);

  return (
    <div className={styles.trendList}>
      {data.map((point) => {
        const cost = parseFloat(point.combined_usd) || 0;
        return (
          <div key={point.date} className={styles.trendRow}>
            <span className={styles.trendPeriod}>{point.date}</span>
            <div
              className={styles.trendBar}
              title={`Agent Console ${formatMoney(point.agent_console_usd)} · AI/API ${formatMoney(point.api_usd)}`}
            >
              <div
                className={styles.trendBarFill}
                style={{ width: `${(cost / maxCost) * 100}%` }}
              />
            </div>
            <span className={styles.trendValue}>{formatMoney(point.combined_usd)}</span>
          </div>
        );
      })}
    </div>
  );
}
